/**
 * Fallback authentication service when Kratos is not enabled
 * Uses direct Hydra OAuth2 flow
 */

const HYDRA_URL = import.meta.env.VITE_HYDRA_URL || 'http://localhost:4444';
const CONTROL_PLANE_API = import.meta.env.VITE_CONTROL_PLANE_API || 'http://localhost:3000';

export interface FallbackLoginRequest {
  username: string;
  password: string;
  tenant_id: string;
}

export interface FallbackLoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
}

export class FallbackAuthService {
  /**
   * Authenticate using fallback mechanism (direct to control plane)
   */
  async login(request: FallbackLoginRequest): Promise<FallbackLoginResponse> {
    try {
      // Call control plane fallback auth endpoint
      const response = await fetch(`${CONTROL_PLANE_API}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-ID': request.tenant_id,
        },
        body: JSON.stringify({
          username: request.username,
          password: request.password,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Login failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Error during fallback login:', error);
      throw error;
    }
  }

  /**
   * Initiate OAuth2 authorization code flow with Hydra
   */
  initiateOAuth2Flow(clientId: string, redirectUri: string, state: string): void {
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid profile email',
      state,
    });

    window.location.href = `${HYDRA_URL}/oauth2/auth?${params.toString()}`;
  }

  /**
   * Exchange authorization code for tokens
   */
  async exchangeCodeForTokens(code: string, clientId: string, redirectUri: string): Promise<FallbackLoginResponse> {
    try {
      const response = await fetch(`${HYDRA_URL}/oauth2/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          code,
          client_id: clientId,
          redirect_uri: redirectUri,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to exchange code for tokens');
      }

      return await response.json();
    } catch (error) {
      console.error('Error exchanging code for tokens:', error);
      throw error;
    }
  }
}

export const fallbackAuthService = new FallbackAuthService();
